import React, { useState, useEffect, useCallback } from 'react';
import { getSlackStatus, startSlackOAuth, disconnectSlack, SlackIntegrationStatus } from '../../services/supabase';
import './SlackIntegration.css';

interface SlackIntegrationProps {
  onStatusChange?: (connected: boolean) => void;
}

const SlackIntegration: React.FC<SlackIntegrationProps> = ({ onStatusChange }) => {
  const [status, setStatus] = useState<SlackIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSlackStatus();
      setStatus(data);
      onStatusChange?.(data.connected);
    } catch (err: any) {
      setError(err.message || 'Failed to load Slack status');
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  useEffect(() => {
    fetchStatus();
    
    // Check URL params for OAuth callback result
    const params = new URLSearchParams(window.location.search);
    const slackResult = params.get('slack');
    if (slackResult === 'success') {
      const team = params.get('team');
      setSuccess(`Successfully connected to ${team || 'Slack workspace'}!`);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      fetchStatus();
    } else if (slackResult === 'error') {
      setError(`Failed to connect: ${params.get('error') || 'Unknown error'}`);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (slackResult === 'cancelled') {
      setError('Slack connection was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchStatus]);

  const handleConnect = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const { oauth_url } = await startSlackOAuth();
      window.location.href = oauth_url;
    } catch (err: any) {
      setError(err.message || 'Failed to start Slack connection');
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Slack? Your team will no longer be able to ask questions via Slack.')) {
      return;
    }
    try {
      setActionLoading(true);
      setError(null);
      await disconnectSlack();
      setSuccess('Slack has been disconnected');
      fetchStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect Slack');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="slack-integration loading">
        <div className="loading-spinner"></div>
        <span>Loading Slack status...</span>
      </div>
    );
  }

  return (
    <div className="slack-integration">
      <div className="integration-header">
        <div className="integration-icon">
          <svg viewBox="0 0 24 24" width="32" height="32">
            <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"/>
            <path fill="#36C5F0" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"/>
            <path fill="#2EB67D" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z"/>
            <path fill="#ECB22E" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
          </svg>
        </div>
        <div className="integration-info">
          <h3>Slack Integration</h3>
          <p>Allow your team to ask compliance questions directly from Slack</p>
        </div>
        <div className={`status-badge ${status?.connected ? 'connected' : 'disconnected'}`}>
          {status?.connected ? '● Connected' : '○ Not Connected'}
        </div>
      </div>

      {error && <div className="integration-error">{error}</div>}
      {success && <div className="integration-success">{success}</div>}

      {status?.connected ? (
        <div className="connected-details">
          <div className="detail-row">
            <span className="detail-label">Workspace:</span>
            <span className="detail-value">{status.team_name}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Connected:</span>
            <span className="detail-value">
              {status.installed_at ? new Date(status.installed_at).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
          <div className="usage-info">
            <h4>How to use:</h4>
            <p>Mention <code>@ComplyDeck</code> in any channel where the bot is added, followed by your question:</p>
            <div className="example-message">
              <code>@ComplyDeck Do we have an encryption policy for data at rest?</code>
            </div>
          </div>
          {status.can_manage && (
            <button 
              className="btn-disconnect" 
              onClick={handleDisconnect}
              disabled={actionLoading}
            >
              {actionLoading ? 'Disconnecting...' : 'Disconnect Slack'}
            </button>
          )}
        </div>
      ) : (
        <div className="disconnected-content">
          <div className="benefits-list">
            <h4>Benefits of Slack integration:</h4>
            <ul>
              <li>✓ Ask compliance questions directly in Slack</li>
              <li>✓ Get answers with evidence links instantly</li>
              <li>✓ Works in any channel where the bot is added</li>
              <li>✓ Responses include confidence scores</li>
            </ul>
          </div>
          {status?.can_manage ? (
            <button className="btn-connect" onClick={handleConnect} disabled={actionLoading}>
              {actionLoading ? 'Connecting...' : 'Connect to Slack'}
            </button>
          ) : (
            <p className="admin-note">Contact your admin to connect Slack</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SlackIntegration;

